import { useEffect, useState } from 'react';
import type { Client } from '@salonomy/types';
import { Avatar, Button, EmptyState, Pill, TextInput } from '@salonomy/ui';
import { ConfirmModal } from '../../components/ConfirmModal.js';
import { CLIENTS_PAGE, useClients } from '../../data/clients.js';
import { useEraseClient, useExportClient } from '../../data/gdpr.js';
import { fullName, initials, maskPhone } from '../../lib/format.js';
import { useToast } from '../../lib/toast.js';
import { downloadJson, exportFilename } from './gdpr-download.js';

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

/**
 * Per-client GDPR: find a client, then export their full record (Article 20) or erase it
 * permanently (Article 17). Manager-only — the parent GdprPanel only mounts this for a manager, so
 * the controls assume that gate is already satisfied.
 *
 * Sealed-book legibility: each row shows whether the client sits in the shared salon book or a
 * renter's sealed book, so the operator knows whose data they are touching. A manager cannot reach
 * INTO a renter's sealed book here — RLS keeps those clients out of this list entirely; the search
 * only returns clients the session can see.
 *
 * Erasure is ONLINE-ONLY with explicit success (typed-confirm "type the client's name to erase").
 * The mutation hits the live endpoint directly — never the offline outbox — so a network failure
 * shows a clear error rather than a "queued" promise we couldn't honour.
 */
export function ClientGdprControls({ ownRenterId }: { ownRenterId: string | null }) {
  const toast = useToast();
  const [q, setQ] = useState('');
  const debouncedQ = useDebounced(q, 250);
  const { data, isLoading } = useClients(debouncedQ);
  const clients = data?.data ?? [];
  const total = data?.pagination.total ?? 0;

  const exportClient = useExportClient();
  const eraseClient = useEraseClient();
  const [exportingId, setExportingId] = useState<string | null>(null);
  const [toErase, setToErase] = useState<Client | null>(null);
  const [erasing, setErasing] = useState(false);

  async function onExport(c: Client) {
    setExportingId(c.id);
    try {
      const bundle = await exportClient.mutateAsync(c.id);
      const name = fullName(c.firstName, c.lastName);
      downloadJson(exportFilename(name), bundle);
      toast.success(`Exported ${name}’s record`);
    } catch {
      // Online-only: a read against the network can't be queued — say so plainly.
      toast.error('Could not export — check your connection and try again.');
    } finally {
      setExportingId(null);
    }
  }

  async function confirmErase() {
    if (!toErase) return;
    const name = fullName(toErase.firstName, toErase.lastName);
    setErasing(true);
    try {
      await eraseClient.mutateAsync(toErase.id);
      toast.success(`${name} permanently erased`);
      setToErase(null);
    } catch {
      toast.error('Erasure failed — nothing was deleted. This action is online-only; try again.');
    } finally {
      setErasing(false);
    }
  }

  return (
    <div>
      <div style={{ fontWeight: 600 }}>Per-client export &amp; erasure</div>
      <div className="muted" style={{ fontSize: 13, margin: '4px 0 12px' }}>
        Give a person a machine-readable copy of everything you hold about them, or erase their
        record for good. Erasure is permanent and online-only — it is never queued offline.
      </div>

      <TextInput
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search by name or phone…"
        style={{ width: '100%', maxWidth: 320 }}
      />

      <div className="settings-list" style={{ marginTop: 12 }}>
        {isLoading && !clients.length ? (
          <div className="muted" style={{ fontSize: 13, padding: '8px 0' }}>
            Loading…
          </div>
        ) : clients.length === 0 ? (
          <EmptyState
            title="No clients found"
            description={debouncedQ ? 'Try a different search.' : 'Search to find a client.'}
          />
        ) : (
          clients.map((c) => {
            const name = fullName(c.firstName, c.lastName);
            const sealed = c.renterId != null;
            const ownSealed = sealed && c.renterId === ownRenterId;
            return (
              <div key={c.id} className="settings-row">
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                  <Avatar initials={initials(name)} tone="slate" />
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 600 }}>{name}</div>
                    <div className="muted" style={{ fontSize: 12 }}>
                      {maskPhone(c.phone)}
                    </div>
                  </div>
                  {sealed ? (
                    <Pill tone="soft">{ownSealed ? 'Your sealed book' : 'Sealed book'}</Pill>
                  ) : null}
                </div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => onExport(c)}
                    disabled={exportingId === c.id}
                  >
                    {exportingId === c.id ? 'Exporting…' : 'Export'}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setToErase(c)}>
                    Erase
                  </Button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {total > CLIENTS_PAGE ? (
        <div className="muted" style={{ fontSize: 12.5, marginTop: 8 }}>
          Showing the first {CLIENTS_PAGE} of {total} — narrow your search to find a specific
          client.
        </div>
      ) : null}

      <ConfirmModal
        open={Boolean(toErase)}
        title="Erase client record"
        confirmLabel="Erase permanently"
        confirmPhrase={toErase ? fullName(toErase.firstName, toErase.lastName) : undefined}
        busy={erasing}
        onConfirm={confirmErase}
        onClose={() => setToErase(null)}
      >
        <p style={{ margin: '0 0 8px' }}>
          This permanently deletes{' '}
          <strong>{toErase ? fullName(toErase.firstName, toErase.lastName) : ''}</strong> — their
          profile, appointment history, notes and colour formulas. It cannot be undone.
        </p>
        <p style={{ margin: '0 0 8px' }}>
          This is a GDPR erasure (Article 17). It runs online only and reports success the moment it
          completes — it is never queued to sync later.
        </p>
        {toErase?.renterId ? (
          <p style={{ margin: 0 }}>
            This client is in a sealed book. Erasing them removes them from that renter’s book only.
          </p>
        ) : null}
      </ConfirmModal>
    </div>
  );
}
